<template>
  <div>
    <div>
      <BaseButton
        :text="`Back to 'Your details'`"
        flat
        ripple
        class="font-weight-bold d-flex ml-n5"
        icon="mdi-chevron-left"
        color="#106175"
        small
        @click="backToYourDetailsStep"
      />

      <div class="d-flex justify-space-between mt-10">
        <h1 class="font-24 font-lg-28 font-weight-bold primary--text">
          Property details
        </h1>
        <BaseButton
          :text="$vuetify.breakpoint.lgAndUp ? 'Save &amp; exit' : 'Save'"
          flat
          depressed
          ripple
          class="font-weight-bold px-1 py-3"
          icon="mdi-content-save"
          color="#106175"
          small
          @click="toggleGlobalSaveAndExitDialog"
        />
      </div>
    </div>

    <ValidationObserver
      ref="observerFormPropertyDetails"
      v-slot="{ validate, invalid }"
    >
      <v-form
        ref="form"
        @submit.prevent="validate().then(() => onSubmit(invalid))"
      >
        <div :class="isEnteringAddressManually ? 'mt-10' : ''">
          <template
            v-if="isShowInsuredAddressLabel && !isEnteringAddressManually"
          >
            <BaseLabel class="mt-10" text="Find your property" />
            <p
              class="d-flex align-left flex-no-wrap font-14 description"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '630px' : '100%',
              }"
            >
              {{ ADDRESS_FINDER_INPUT_DESCRIPTION }}
            </p>
          </template>
          <BaseAddressLookup
            id="address-lookup-insuredAddress"
            data-uiid="InsuredAddress"
            manualFormUFiid="InsuredAddress"
            :placeholder="
              $vuetify.breakpoint.lgAndUp
                ? 'Start typing your address or postcode...'
                : 'Start typing...'
            "
            showManualAddressForm
            manualAddressFormText="Your address"
            :hideDetails="false"
            :address="insuredAddress"
            @address-selected="
              (response) => addressSelected(response, insuredAddress)
            "
            @is-address-selected="insuredAddressLabel"
            @use-address-finder="isEnteringAddressManually = false"
            @enter-address-manually="isEnteringAddressManually = true"
            :rules="{
              required: {
                required: true,
                fieldLabel: 'Find your property',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          />
        </div>

        <BaseLabel
          :class="
            isEnteringAddressManually || isShowInsuredAddressLabel
              ? 'mt-3'
              : 'mt-10'
          "
          :text="LABEL_CONSTANTS.TERM_OF_INSURANCE"
        />
        <BaseRadio
          class="mt-2"
          v-model="termOfInsurance.Value"
          pslStyle="button"
          :items="termOfInsuranceOptions"
          dataUiid="TermOfInsurance"
          :hideDetails="false"
          :rules="{
            required: {
              required: true,
              fieldLabel: LABEL_CONSTANTS.TERM_OF_INSURANCE,
            },
          }"
          :key="`${termOfInsurance.Value}_TermOfInsurance`"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :width="$vuetify.breakpoint.lgAndUp ? '177' : 'calc(50% - 6px)'"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          aria-label="Term of insurance"
        />

        <BaseButton
          rounded
          class="mt-5 font-weight-bold continue-button primary--text"
          height="60"
          type="submit"
          text="GET QUOTES"
          :color="'psorange'"
          :fontSize="'font-20'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '263' : '100%'"
        />
      </v-form>
    </ValidationObserver>

    <EligibilityBaseDialog ref="dialog" :windowClass="'confirm-dialog'">
      <template v-slot:content="{ cancel, isShow, title }">
        <Eligibility
          :cancel="cancel"
          :dialogRef="$refs.dialog"
          :dialogLoading="$refs.dialogLoading"
          :dialogNotMet="$refs.dialogNotMet"
          :isShow="isShow"
          :listEligibility="TENANTS_LIABILITY_ELIGIBILITY"
          :inforMsg="QUOTE_ELIGIBILITY_INFO_MSG"
          :title="title"
          @confirmEligibility="createQuote()"
          :customHandleConfirmEligibility="true"
        />
      </template>
    </EligibilityBaseDialog>

    <EligibilityBaseDialog ref="dialogLoading" :windowClass="'confirm-dialog'">
      <template v-slot:content="{ title }">
        <EligibilityLoading
          :title="title"
          backgroundMessageColor="var(--blue-100)"
          borderWidth="1px"
          borderStyle="solid"
          borderColor="var(--blue-500)"
          borderRadius="4px"
          imageSource=""
          :gettingQuoteMessage="GETTING_QUOTE_TENANTS_LIABILITY_MESSAGE"
        />
      </template>
    </EligibilityBaseDialog>

    <EligibilityBaseDialog ref="dialogNotMet" :windowClass="'confirm-dialog'">
      <template v-slot:content="{ cancel, isShow, title }">
        <EligibilityNotMet
          :cancel="cancel"
          :dialogRef="$refs.dialog"
          :dialogNotMet="$refs.dialogNotMet"
          :isShow="isShow"
          :title="title"
        />
      </template>
    </EligibilityBaseDialog>
  </div>
</template>

<script>
import { EventBus } from "@/common/eventbus.js";
import { scrollTop } from "@/common/utils";
import { ValidationObserver } from "vee-validate";
import { mapActions, mapState } from "vuex";

import {
  setItemDataUiids,
  TERM_OF_INSURANCE_OPTIONS,
} from "@/constants/component.constants";
import { ADDRESS_FINDER_INPUT_DESCRIPTION } from "@/constants/description.constants";
import {
  QUOTE_ELIGIBILITY_INFO_MSG,
  TENANTS_LIABILITY_ELIGIBILITY,
} from "@/constants/eligibility.constants";
import * as LABEL_CONSTANTS from "@/constants/label.constants";
import { GETTING_QUOTE_TENANTS_LIABILITY_MESSAGE } from "@/constants/message.constants";
import { ROUTE_NAME } from "@/constants/route.constants";
import { QUESTIONS_MODULE } from "@/store/types/module-types";

import BaseAddressLookup from "@/components/BaseComponents/BaseAddressLookup.vue";
import BaseButton from "@/components/BaseComponents/BaseButton.vue";
import BaseLabel from "@/components/BaseComponents/BaseLabel.vue";
import BaseRadio from "@/components/BaseComponents/BaseRadio.vue";
import EligibilityBaseDialog from "@/components/EligibilityBaseDialog";
import Eligibility from "@/views/Dialog/Eligibility.vue";
import EligibilityLoading from "@/views/Dialog/EligibilityLoading";
import EligibilityNotMet from "@/views/Dialog/EligibilityNotMet.vue";
import { addressSelected } from "@/common/addresses";

export default {
  name: "PropertyDetailsPage",
  components: {
    BaseButton,
    BaseLabel,
    BaseRadio,
    ValidationObserver,
    BaseAddressLookup,
    EligibilityLoading,
    EligibilityNotMet,
    EligibilityBaseDialog,
    Eligibility,
  },
  data() {
    return {
      LABEL_CONSTANTS,
      TENANTS_LIABILITY_ELIGIBILITY,
      QUOTE_ELIGIBILITY_INFO_MSG,
      GETTING_QUOTE_TENANTS_LIABILITY_MESSAGE,
      ADDRESS_FINDER_INPUT_DESCRIPTION,
      isShowInsuredAddressLabel: true,
      isEnteringAddressManually: false,
      insuredAddress: {},
      termOfInsurance: {},
    };
  },
  computed: {
    ...mapState(QUESTIONS_MODULE, ["restoringLastStep", "questionMap"]),
    termOfInsuranceOptions() {
      return setItemDataUiids("TermOfInsurance", TERM_OF_INSURANCE_OPTIONS);
    },
  },
  created() {
    scrollTop();
    this.onSetupAndLoadSavedData();
  },
  mounted() {
    this.setRestoringLastStep(false);
    this.setCurrentRefObserverForm(this.$refs.observerFormPropertyDetails);
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "getQuotes",
      "saveProgress",
      "setRestoringLastStep",
      "setIsExpiredFlag",
      "toggleGlobalSaveAndExitDialog",
      "setCurrentRefObserverForm",
      "refreshSessionAndNextStep",
    ]),
    addressSelected,
    backToYourDetailsStep() {
      this.setRestoringLastStep(false);
      this.$router.push({
        name: ROUTE_NAME.TENANTS_LIABILITY.B2B.YOUR_DETAILS,
      });
    },
    onSetupAndLoadSavedData() {
      this.termOfInsurance = this.questionMap.TermOfInsurance;
      // insured address
      this.insuredAddress.PostCode = this.questionMap.InsuredAddressPostCode;
      this.insuredAddress.Street = this.questionMap.InsuredAddressStreet;
      this.insuredAddress.District = this.questionMap.InsuredAddressDistrict;
      this.insuredAddress.Town = this.questionMap.InsuredAddressTown;
      this.insuredAddress.County = this.questionMap.InsuredAddressCounty;
    },

    async onSubmit(invalid) {
      if (invalid) {
        EventBus.$emit("validation-error", "Validation error occurred");
        return;
      }
      this.$refs.dialog.open("Eligibility check");
    },

    createQuote() {
      this.$refs.dialog.cancel();
      this.$refs.dialogLoading.open("Hang tight, getting quotes...");
      this.questionMap.TenancyAgreement.Value = true;
      this.questionMap.MainUKResidence.Value = true;

      const quoteRequestFields = {
        IsPartialQuote: false,
      };

      this.getQuotes(quoteRequestFields)
        .then(() => {
          this.setIsExpiredFlag(false);
          this.$refs.dialog.cancel();
          this.refreshSessionAndNextStep(
            ROUTE_NAME.TENANTS_LIABILITY.B2B.QUOTATION_RESULT
          );
        })
        .catch(() => {
          this.$refs.dialogLoading.cancel();
        });
    },
    insuredAddressLabel(val) {
      this.isShowInsuredAddressLabel = !val;
    },
  },
};
</script>

<style lang="scss" scoped>
.description {
  letter-spacing: 0.4px;
  margin-bottom: 13px;
  max-width: 665px;
  color: #272727;
}
</style>
